<template>
  <div class="card-management">
    <div class="page-header">
      <h1>卡密管理</h1>
      <div class="header-actions">
        <el-button
          v-if="selectedCards.length > 0"
          type="danger"
          @click="batchDeleteCards"
          :disabled="selectedCards.some(card => card.status === 'active')"
        >
          批量删除 ({{ selectedCards.length }})
        </el-button>
        <el-button
          v-if="selectedCards.length > 0"
          type="success"
          @click="exportSelectedCards"
        >
          导出选中
        </el-button>
        <el-button type="info" @click="exportAllCards">
          导出全部
        </el-button>
        <el-button type="primary" @click="showGenerateDialog = true">
          <el-icon><Plus /></el-icon>
          生成卡密
        </el-button>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-content">
              <div class="stat-number">{{ stats.overview?.total || 0 }}</div>
              <div class="stat-label">总卡密数</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-content">
              <div class="stat-number">{{ stats.overview?.active || 0 }}</div>
              <div class="stat-label">已激活</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-content">
              <div class="stat-number">{{ stats.overview?.unused || 0 }}</div>
              <div class="stat-label">未使用</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-content">
              <div class="stat-number">{{ stats.overview?.expired || 0 }}</div>
              <div class="stat-label">已过期</div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 搜索和筛选 -->
    <el-card class="filter-card">
      <el-form :model="filters" inline>
        <el-form-item label="卡密搜索">
          <el-input v-model="filters.search" placeholder="输入卡密关键字" clearable />
        </el-form-item>
        <el-form-item label="卡密类型">
          <el-select v-model="filters.card_type" placeholder="选择类型" clearable>
            <el-option label="AI主机基础版" value="AI_HOST_BASIC" />
            <el-option label="AI主机专业版" value="AI_HOST_PRO" />
            <el-option label="AI盒子基础版" value="AI_BOX_BASIC" />
            <el-option label="AI盒子专业版" value="AI_BOX_PRO" />
            <el-option label="全功能版" value="ALL_IN_ONE" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="filters.status" placeholder="选择状态" clearable>
            <el-option label="未使用" value="unused" />
            <el-option label="已激活" value="active" />
            <el-option label="已过期" value="expired" />
            <el-option label="已禁用" value="disabled" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadCards">搜索</el-button>
          <el-button @click="resetFilters">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 卡密列表 -->
    <el-card class="table-card">
      <el-table
        :data="cards"
        v-loading="loading"
        stripe
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" />
        <el-table-column prop="card_key" label="卡密" width="250">
          <template #default="{ row }">
            <div class="card-key-cell">
              <span class="card-key-text">{{ row.card_key }}</span>
              <el-button
                size="small"
                type="primary"
                @click="copyCardKey(row.card_key)"
                class="copy-btn"
                title="复制卡密"
              >
                <el-icon><DocumentCopy /></el-icon>
              </el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="card_type" label="类型" width="120">
          <template #default="{ row }">
            <el-tag :type="getCardTypeColor(row.card_type)">
              {{ getCardTypeName(row.card_type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="duration_days" label="有效期" width="80">
          <template #default="{ row }">
            {{ row.duration_days }}天
          </template>
        </el-table-column>
        <el-table-column prop="device_limit" label="设备限制" width="80" />
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusColor(row.status)">
              {{ getStatusName(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="creator_name" label="创建者" width="100" />
        <el-table-column prop="created_at" label="创建时间" width="160">
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column prop="activated_at" label="激活时间" width="160">
          <template #default="{ row }">
            {{ row.activated_at ? formatDate(row.activated_at) : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="expire_at" label="过期时间" width="160">
          <template #default="{ row }">
            {{ row.expire_at ? formatDate(row.expire_at) : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="editCard(row)">编辑</el-button>
            <el-button 
              size="small" 
              :type="row.status === 'disabled' ? 'success' : 'warning'"
              @click="toggleCardStatus(row)"
            >
              {{ row.status === 'disabled' ? '启用' : '禁用' }}
            </el-button>
            <el-button 
              size="small" 
              type="danger" 
              @click="deleteCard(row)"
              :disabled="row.status === 'active'"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.limit"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="loadCards"
          @current-change="loadCards"
        />
      </div>
    </el-card>

    <!-- 编辑卡密对话框 -->
    <el-dialog
      v-model="showEditDialog"
      title="编辑卡密"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="editFormRef"
        :model="editForm"
        :rules="editRules"
        label-width="100px"
      >
        <el-form-item label="卡密" prop="card_key">
          <el-input v-model="editForm.card_key" disabled />
        </el-form-item>
        <el-form-item label="卡密类型" prop="card_type">
          <el-select v-model="editForm.card_type" placeholder="请选择卡密类型">
            <el-option label="AI主机基础版" value="AI_HOST_BASIC" />
            <el-option label="AI主机专业版" value="AI_HOST_PRO" />
            <el-option label="AI盒子基础版" value="AI_BOX_BASIC" />
            <el-option label="AI盒子专业版" value="AI_BOX_PRO" />
            <el-option label="全功能版" value="ALL_IN_ONE" />
          </el-select>
        </el-form-item>
        <el-form-item label="有效期" prop="duration_days">
          <el-input-number
            v-model="editForm.duration_days"
            :min="1"
            :max="3650"
            controls-position="right"
          />
          <span style="margin-left: 10px;">天</span>
        </el-form-item>
        <el-form-item label="设备限制" prop="device_limit">
          <el-input-number
            v-model="editForm.device_limit"
            :min="1"
            :max="10"
            controls-position="right"
          />
          <span style="margin-left: 10px;">台设备</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showEditDialog = false">取消</el-button>
          <el-button type="primary" @click="updateCard" :loading="editing">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 生成卡密对话框 -->
    <el-dialog v-model="showGenerateDialog" title="生成卡密" width="500px">
      <el-form :model="generateForm" :rules="generateRules" ref="generateFormRef" label-width="100px">
        <el-form-item label="卡密类型" prop="card_type">
          <el-select v-model="generateForm.card_type" placeholder="选择卡密类型">
            <el-option label="AI主机基础版" value="AI_HOST_BASIC" />
            <el-option label="AI主机专业版" value="AI_HOST_PRO" />
            <el-option label="AI盒子基础版" value="AI_BOX_BASIC" />
            <el-option label="AI盒子专业版" value="AI_BOX_PRO" />
            <el-option label="全功能版" value="ALL_IN_ONE" />
          </el-select>
        </el-form-item>
        <el-form-item label="有效期" prop="duration_days">
          <el-input-number v-model="generateForm.duration_days" :min="1" :max="3650" />
          <span style="margin-left: 10px;">天</span>
        </el-form-item>
        <el-form-item label="设备限制" prop="device_limit">
          <el-input-number v-model="generateForm.device_limit" :min="1" :max="100" />
        </el-form-item>
        <el-form-item label="生成数量" prop="quantity">
          <el-input-number v-model="generateForm.quantity" :min="1" :max="100" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showGenerateDialog = false">取消</el-button>
        <el-button type="primary" @click="generateCards" :loading="generating">生成</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, DocumentCopy } from '@element-plus/icons-vue'
import { cardAPI } from '@/services/api'

export default {
  name: 'CardManagement',
  components: {
    Plus,
    DocumentCopy
  },
  setup() {
    const loading = ref(false)
    const generating = ref(false)
    const editing = ref(false)
    const showGenerateDialog = ref(false)
    const showEditDialog = ref(false)
    const cards = ref([])
    const stats = ref({})
    const selectedCards = ref([])
    
    const filters = reactive({
      search: '',
      card_type: '',
      status: ''
    })
    
    const pagination = reactive({
      page: 1,
      limit: 20,
      total: 0
    })
    
    const generateForm = reactive({
      card_type: '',
      duration_days: 30,
      device_limit: 1,
      quantity: 1
    })
    
    const generateRules = {
      card_type: [{ required: true, message: '请选择卡密类型', trigger: 'change' }],
      duration_days: [{ required: true, message: '请输入有效期', trigger: 'blur' }],
      device_limit: [{ required: true, message: '请输入设备限制', trigger: 'blur' }],
      quantity: [{ required: true, message: '请输入生成数量', trigger: 'blur' }]
    }

    const editForm = reactive({
      id: null,
      card_key: '',
      card_type: '',
      duration_days: 30,
      device_limit: 1
    })

    const editRules = {
      card_type: [{ required: true, message: '请选择卡密类型', trigger: 'change' }],
      duration_days: [{ required: true, message: '请输入有效期', trigger: 'blur' }],
      device_limit: [{ required: true, message: '请输入设备限制', trigger: 'blur' }]
    }
    
    // 加载卡密列表
    const loadCards = async () => {
      loading.value = true
      try {
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          ...filters
        }
        const response = await cardAPI.getCards(params)
        cards.value = response.data.cards
        pagination.total = response.data.pagination.total
      } catch (error) {
        ElMessage.error('加载卡密列表失败: ' + error.message)
      } finally {
        loading.value = false
      }
    }
    
    // 加载统计数据
    const loadStats = async () => {
      try {
        const response = await cardAPI.getCardStats()
        stats.value = response.data
      } catch (error) {
        ElMessage.error('加载统计数据失败: ' + error.message)
      }
    }
    
    // 生成卡密
    const generateCards = async () => {
      // 表单验证逻辑
      generating.value = true
      try {
        await cardAPI.generateCards(generateForm)
        ElMessage.success(`成功生成${generateForm.quantity}个卡密`)
        showGenerateDialog.value = false
        loadCards()
        loadStats()
      } catch (error) {
        ElMessage.error('生成卡密失败: ' + error.message)
      } finally {
        generating.value = false
      }
    }
    
    // 切换卡密状态
    const toggleCardStatus = async (card) => {
      const newStatus = card.status === 'disabled' ? 'unused' : 'disabled'
      const action = newStatus === 'disabled' ? '禁用' : '启用'
      
      try {
        await ElMessageBox.confirm(`确定要${action}卡密 ${card.card_key} 吗？`, '确认操作')
        await cardAPI.updateCardStatus(card.id, { status: newStatus })
        ElMessage.success(`${action}成功`)
        loadCards()
        loadStats()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(`${action}失败: ` + error.message)
        }
      }
    }
    
    // 删除卡密
    const deleteCard = async (card) => {
      try {
        await ElMessageBox.confirm(`确定要删除卡密 ${card.card_key} 吗？此操作不可恢复！`, '确认删除', {
          type: 'warning'
        })
        await cardAPI.deleteCard(card.id)
        ElMessage.success('删除成功')
        loadCards()
        loadStats()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败: ' + error.message)
        }
      }
    }
    
    // 重置筛选条件
    const resetFilters = () => {
      Object.assign(filters, {
        search: '',
        card_type: '',
        status: ''
      })
      pagination.page = 1
      loadCards()
    }

    // 处理表格选择变化
    const handleSelectionChange = (selection) => {
      selectedCards.value = selection
    }

    // 批量删除卡密
    const batchDeleteCards = async () => {
      const activeCards = selectedCards.value.filter(card => card.status === 'active')
      if (activeCards.length > 0) {
        ElMessage.warning('选中的卡密中包含已激活的卡密，无法删除')
        return
      }

      try {
        await ElMessageBox.confirm(
          `确定要删除选中的 ${selectedCards.value.length} 个卡密吗？此操作不可恢复！`,
          '批量删除确认',
          {
            type: 'warning'
          }
        )

        const deletePromises = selectedCards.value.map(card => cardAPI.deleteCard(card.id))
        await Promise.all(deletePromises)

        ElMessage.success(`成功删除 ${selectedCards.value.length} 个卡密`)
        selectedCards.value = []
        loadCards()
        loadStats()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('批量删除失败: ' + error.message)
        }
      }
    }

    // 导出选中的卡密
    const exportSelectedCards = () => {
      if (selectedCards.value.length === 0) {
        ElMessage.warning('请先选择要导出的卡密')
        return
      }
      exportCards(selectedCards.value)
    }

    // 导出所有卡密
    const exportAllCards = () => {
      if (cards.value.length === 0) {
        ElMessage.warning('没有可导出的卡密')
        return
      }
      exportCards(cards.value)
    }

    // 导出卡密数据
    const exportCards = (cardsToExport) => {
      const csvContent = [
        ['卡密', '类型', '有效期(天)', '设备限制', '状态', '创建时间', '激活时间', '过期时间'].join(','),
        ...cardsToExport.map(card => [
          card.card_key,
          getCardTypeName(card.card_type),
          card.duration_days,
          card.device_limit,
          getStatusName(card.status),
          formatDate(card.created_at),
          card.activated_at ? formatDate(card.activated_at) : '',
          card.expire_at ? formatDate(card.expire_at) : ''
        ].join(','))
      ].join('\n')

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `卡密数据_${new Date().toISOString().slice(0, 10)}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      ElMessage.success(`成功导出 ${cardsToExport.length} 个卡密`)
    }

    // 复制卡密
    const copyCardKey = async (cardKey) => {
      try {
        await navigator.clipboard.writeText(cardKey)
        ElMessage.success('卡密已复制到剪贴板')
      } catch (error) {
        // 如果现代API不可用，使用传统方法
        const textArea = document.createElement('textarea')
        textArea.value = cardKey
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          ElMessage.success('卡密已复制到剪贴板')
        } catch (err) {
          ElMessage.error('复制失败，请手动复制')
        }
        document.body.removeChild(textArea)
      }
    }

    // 编辑卡密
    const editCard = (card) => {
      if (card.status !== 'unused') {
        ElMessage.warning('只有未使用的卡密才能编辑')
        return
      }

      Object.assign(editForm, {
        id: card.id,
        card_key: card.card_key,
        card_type: card.card_type,
        duration_days: card.duration_days,
        device_limit: card.device_limit
      })

      showEditDialog.value = true
    }

    // 更新卡密
    const updateCard = async () => {
      try {
        editing.value = true

        const response = await cardAPI.updateCard(editForm.id, {
          card_type: editForm.card_type,
          duration_days: editForm.duration_days,
          device_limit: editForm.device_limit
        })

        if (response.success) {
          ElMessage.success('卡密更新成功')
          showEditDialog.value = false
          loadCards()
        } else {
          ElMessage.error(response.message || '更新失败')
        }
      } catch (error) {
        ElMessage.error('更新卡密失败: ' + error.message)
      } finally {
        editing.value = false
      }
    }
    
    // 工具函数
    const getCardTypeName = (type) => {
      const names = {
        'AI_HOST_BASIC': 'AI主机基础版',
        'AI_HOST_PRO': 'AI主机专业版',
        'AI_BOX_BASIC': 'AI盒子基础版',
        'AI_BOX_PRO': 'AI盒子专业版',
        'ALL_IN_ONE': '全功能版'
      }
      return names[type] || type
    }
    
    const getCardTypeColor = (type) => {
      const colors = {
        'AI_HOST_BASIC': '',
        'AI_HOST_PRO': 'success',
        'AI_BOX_BASIC': 'info',
        'AI_BOX_PRO': 'warning',
        'ALL_IN_ONE': 'danger'
      }
      return colors[type] || ''
    }
    
    const getStatusName = (status) => {
      const names = {
        'unused': '未使用',
        'active': '已激活',
        'expired': '已过期',
        'disabled': '已禁用'
      }
      return names[status] || status
    }
    
    const getStatusColor = (status) => {
      const colors = {
        'unused': 'info',
        'active': 'success',
        'expired': 'warning',
        'disabled': 'danger'
      }
      return colors[status] || ''
    }
    
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleString('zh-CN')
    }
    
    onMounted(() => {
      loadCards()
      loadStats()
    })
    
    return {
      loading,
      generating,
      editing,
      showGenerateDialog,
      showEditDialog,
      cards,
      stats,
      selectedCards,
      filters,
      pagination,
      generateForm,
      generateRules,
      editForm,
      editRules,
      loadCards,
      loadStats,
      generateCards,
      editCard,
      updateCard,
      toggleCardStatus,
      deleteCard,
      resetFilters,
      handleSelectionChange,
      batchDeleteCards,
      exportSelectedCards,
      exportAllCards,
      copyCardKey,
      getCardTypeName,
      getCardTypeColor,
      getStatusName,
      getStatusColor,
      formatDate
    }
  }
}
</script>

<style scoped>
.card-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.card-key-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-key-text {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.copy-btn {
  flex-shrink: 0;
  padding: 4px 8px;
  min-width: auto;
}

.stats-cards {
  margin-bottom: 20px;
}

.stat-card {
  text-align: center;
}

.stat-content {
  padding: 20px;
}

.stat-number {
  font-size: 32px;
  font-weight: bold;
  color: #409eff;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.filter-card,
.table-card {
  margin-bottom: 20px;
}

.pagination-wrapper {
  margin-top: 20px;
  text-align: right;
}
</style>
